import { hubFetch } from '@core/auth/services/hub-fetch';
import { Injectable, inject } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { environment } from '@env/environment';
import { Auth } from '@core/auth/services/auth';
import type {
  RunAction,
  StageCounts,
  WorkItem,
  WorkItemsQuery,
  WorkItemsResponse,
  WorkflowCountsResponse,
  WorkflowRunRequest,
} from '@features/campaigns/models/campaign-workflow.model';
import {
  Campaign,
  CreateCampaignDto,
  UpdateCampaignDto,
  MarkPaidDto,
  CampaignWithDetails,
  CsvValidationResult,
  CampaignProgress,
  PackageType,
  PackageConfig,
  AddressPreview,
  OutlierAddress,
  SiValidationResult,
  CampaignAnalysesStats,
} from '@features/campaigns/models/campaign.model';

/** A town or district to frame the campaign area map; bounds are [south, west, north, east]. */
export interface PlaceResult {
  label: string;
  latitude: number;
  longitude: number;
  bounds: [number, number, number, number] | null;
}
export interface CampaignListResponse {
  campaigns: Campaign[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CampaignResponse {
  data: Campaign;
}

export interface ApiResponse {
  success: boolean;
  message: string;
}

export interface BulkFileTag {
  tag: string;
  type: '2d' | '3d';
  label: string;
}

export interface BulkFilesAvailabilityResponse {
  availableTags: BulkFileTag[];
}

/** Lightweight analysis row returned by ?projection=selection — just enough to drive "select all". */
export interface CampaignSelectionRow {
  id: string;
  analysisId: string;
  title: string | null;
  technicalState: string | null;
  technicalStage: string | null;
  technicalTask: number | null;
  analysisType: string | null;
}

/** A bulk-run working set expressed as a query, never an enumerated ID list. */
export interface BulkRunSelection {
  mode: 'all' | 'filtered' | 'explicit';
  filterStates?: string[];
  filterStages?: string[];
  explicitIds?: string[];
}

/** Counter snapshot for a bulk run — returned by create/get and pushed over SSE. */
export interface BulkRunSnapshot {
  runId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  total: number;
  dispatched: number;
  succeeded: number;
  failed: number;
  skipped: number;
  startedAt?: string | null;
  completed?: number;
  pipelineFailed?: number;
  processing?: number;
  queued?: number;
  analysisUpdates?: Array<{
    id: string;
    technicalState: string;
    technicalTask: number;
    technicalStage: string;
  }>;
}

export type BulkRunEvent =
  | ({ type: 'run_progress' } & BulkRunSnapshot)
  | ({ type: 'run_complete' } & BulkRunSnapshot);

/**
 * Live progress of the current execution, scoped to the most recent run's analyses.
 * `dispatched/skipped/failed` are the run's DISPATCH outcome; `completed/pipelineFailed/
 * processing/queued` are the live pipeline states of just this run's analyses.
 */
export interface ExecutionProgress extends BulkRunSnapshot {
  /** ISO time the run started — analyses touched at/after this belong to this run. */
  startedAt: string;
  completed: number;
  pipelineFailed: number;
  processing: number;
  queued: number;
}

/** A past or current run as shown in the execution-history tab (full descriptor, not trimmed). */
export interface BulkRunHistoryItem extends BulkRunSnapshot {
  selectionMode: 'all' | 'filtered' | 'explicit';
  filterStates: string[];
  filterStages: string[];
  explicitCount: number;
  startTask: number;
  endTask: number;
  analysisType: string | null;
  templateId: string | null;
  /** Null for older task-range runs. */
  action: RunAction | null;
  /** Members per stage when the run finished, plus skipped; null while running. */
  result: Record<string, number> | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string | null };
}

export interface Paginated<T> {
  items: T[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignsService {
  private readonly baseApiUrl = environment.apiUrl;
  private readonly auth = inject(Auth);

  /**
   * Get authorization headers for API requests
   */
  private getAuthHeaders(): HeadersInit {

    return {
      'Content-Type': 'application/json',

    };
  }

  /**
   * Handle API errors and transform to user-friendly messages
   */
  private async handleApiError(response: Response): Promise<never> {
    let errorMessage = 'Ein unerwarteter Fehler ist aufgetreten.';

    try {
      const errorData = (await response.json()) as {
        message?: string;
        error?: string | { message?: string };
      };
      errorMessage =
        (typeof errorData.error === 'object' && errorData.error?.message) ||
        errorData.message ||
        (typeof errorData.error === 'string' ? errorData.error : errorMessage);
    } catch {
      // If JSON parsing fails, use status text
      errorMessage = response.statusText || errorMessage;
    }

    // The API answers in English; a missing record is the one case people meet in normal use.
    if (response.status === 404) {
      errorMessage = 'Dieser Eintrag wurde nicht gefunden. Er wurde gelöscht oder Sie haben keinen Zugriff.';
    }
    const error = new Error(errorMessage) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetchApi<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseApiUrl}${endpoint}`;

    const response = await hubFetch(url, {
      headers: this.getAuthHeaders(),
      ...options,
    });

    if (!response.ok) {
      await this.handleApiError(response);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Create a new campaign
   */
  async createCampaign(
    organizationId: string,
    data: CreateCampaignDto,
  ): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  /**
   * Get all campaigns for an organization
   */
  async getCampaigns(organizationId: string): Promise<Campaign[]> {
    const response = await this.fetchApi<CampaignListResponse>(
      `/organizations/${organizationId}/campaigns`,
    );
    return response.campaigns;
  }

  /**
   * Get campaign by ID
   */
  async getCampaignById(organizationId: string, id: string): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns/${id}`,
    );
  }

  /**
   * Get campaign with full details (including analyses)
   */
  async getCampaignDetails(
    organizationId: string,
    id: string,
  ): Promise<CampaignWithDetails> {
    return this.fetchApi<CampaignWithDetails>(
      `/organizations/${organizationId}/campaigns/${id}/details`,
    );
  }

  /**
   * Update an existing campaign
   */
  async updateCampaign(
    organizationId: string,
    id: string,
    data: UpdateCampaignDto,
  ): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  /**
   * Soft delete a campaign
   */
  async deleteCampaign(organizationId: string, id: string): Promise<void> {
    return this.fetchApi<void>(
      `/organizations/${organizationId}/campaigns/${id}`,
      {
        method: 'DELETE',
      },
    );
  }

  /**
   * Restore a soft deleted campaign
   */
  async restoreCampaign(organizationId: string, id: string): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns/${id}/restore`,
      {
        method: 'POST',
      },
    );
  }

  /**
   * Get paginated campaign analyses from Hub DB with aggregate stats.
   */
  async getCampaignAnalyses(
    organizationId: string,
    id: string,
    page = 1,
    limit = 20,
  ): Promise<{
    analyses: any[];
    stats: CampaignAnalysesStats;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    return this.fetchApi<{
      analyses: any[];
      stats: CampaignAnalysesStats;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/organizations/${organizationId}/campaigns/${id}/analyses?page=${page}&limit=${limit}`);
  }

  /**
   * Start a bulk pipeline run from a selection descriptor (all / filtered / explicit).
   * Returns the initial run snapshot (incl. runId + total) immediately; the backend
   * dispatches in batches. Poll getBulkRun() or subscribe to watchBulkRunEvents().
   */
  // ─── Campaign workflow: one stage per analysis ──────────────────────────────

  private workflowUrl(org: string, campaign: string, path = ''): string {
    return `/organizations/${org}/campaigns/${campaign}/workflow${path}`;
  }

  getWorkItems(org: string, campaign: string, query: WorkItemsQuery = {}): Promise<WorkItemsResponse> {
    const params = new URLSearchParams();
    if (query.stage) params.set('stage', query.stage);
    if (query.q?.trim()) params.set('q', query.q.trim());
    if (query.runId) params.set('runId', query.runId);
    if (query.corrected) params.set('corrected', 'true');
    if (query.recheck) params.set('recheck', 'true');
    params.set('page', String(query.page ?? 1));
    params.set('limit', String(query.limit ?? 500));
    return this.fetchApi(this.workflowUrl(org, campaign, `/items?${params}`));
  }

  getWorkflowCounts(org: string, campaign: string): Promise<WorkflowCountsResponse> {
    return this.fetchApi(this.workflowUrl(org, campaign, '/counts'));
  }

  async getWorkItem(org: string, campaign: string, id: string): Promise<WorkItem> {
    return (await this.fetchApi<{ data: WorkItem }>(this.workflowUrl(org, campaign, `/analyses/${id}`))).data;
  }

  async reviewWorkItem(org: string, campaign: string, id: string, body: { key: string; decision: 'approved' | 'excluded' | 'seen' | 'flagged' | 'checked'; reason?: string }): Promise<WorkItem> {
    return (await this.fetchApi<{ data: WorkItem }>(this.workflowUrl(org, campaign, `/reviews/${id}`), { method: 'POST', body: JSON.stringify(body) })).data;
  }

  async undoReview(org: string, campaign: string, id: string): Promise<WorkItem> {
    return (await this.fetchApi<{ data: WorkItem }>(this.workflowUrl(org, campaign, `/reviews/${id}`), { method: 'DELETE' })).data;
  }

  reviewMany(org: string, campaign: string, body: { ids: string[]; decision: 'approved' | 'excluded'; reason?: string }): Promise<{ updated: number; skipped: Record<string, number> }> {
    return this.fetchApi(this.workflowUrl(org, campaign, '/reviews'), { method: 'POST', body: JSON.stringify(body) });
  }

  previewRun(org: string, campaign: string, body: WorkflowRunRequest): Promise<{ runnable: number; skipped: Record<string, number> }> {
    return this.fetchApi(this.workflowUrl(org, campaign, '/runs/preview'), { method: 'POST', body: JSON.stringify(body) });
  }

  startRun(org: string, campaign: string, body: WorkflowRunRequest): Promise<{ data: BulkRunSnapshot; runnable: number; skipped: Record<string, number> }> {
    return this.fetchApi(this.workflowUrl(org, campaign, '/runs'), { method: 'POST', body: JSON.stringify(body) });
  }

  cancelRun(org: string, campaign: string, runId: string): Promise<{ data: BulkRunSnapshot }> {
    return this.fetchApi(this.workflowUrl(org, campaign, `/runs/${runId}/cancel`), { method: 'POST' });
  }

  async createBulkRun(
    organizationId: string,
    campaignId: string,
    body: {
      selection: BulkRunSelection;
      startTask: number;
      endTask: number;
      analysisType?: '2d' | '3d';
      templateId?: string;
    },
  ): Promise<BulkRunSnapshot> {
    return this.fetchApi<BulkRunSnapshot>(
      `/organizations/${organizationId}/campaigns/${campaignId}/bulk-runs`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  /** Fetch a run's current state — used to hydrate on reconnect / poll. */
  async getBulkRun(
    organizationId: string,
    campaignId: string,
    runId: string,
  ): Promise<BulkRunSnapshot> {
    return this.fetchApi<BulkRunSnapshot>(
      `/organizations/${organizationId}/campaigns/${campaignId}/bulk-runs/${runId}`,
    );
  }

  /** The active run for a campaign (if any) — lets the UI re-attach after reload. */
  async getActiveBulkRun(
    organizationId: string,
    campaignId: string,
  ): Promise<{ run: BulkRunSnapshot | null }> {
    return this.fetchApi<{ run: BulkRunSnapshot | null }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/bulk-runs`,
    );
  }

  /** Live, run-scoped progress of the current execution (null if the campaign has never run). */
  async getExecutionProgress(
    organizationId: string,
    campaignId: string,
  ): Promise<ExecutionProgress | null> {
    const res = await this.fetchApi<{ progress: ExecutionProgress | null }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/execution-progress`,
    );
    return res.progress;
  }

  /** Paginated run history for a campaign (newest first) — powers the executions tab. */
  async listBulkRuns(
    organizationId: string,
    campaignId: string,
    page = 1,
    limit = 20,
  ): Promise<Paginated<BulkRunHistoryItem>> {
    const res = await this.fetchApi<{
      data: BulkRunHistoryItem[];
      meta: { total: number };
    }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/bulk-runs/history?page=${page}&limit=${limit}`,
    );
    return { items: res.data, total: res.meta.total };
  }

  /** Per execution: how many of its analyses are in which stage now. */
  async getRunStages(organizationId: string, campaignId: string): Promise<Record<string, Partial<StageCounts>>> {
    const res = await this.fetchApi<{ data: Record<string, Partial<StageCounts>> }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/workflow/run-stages`,
    );
    return res.data;
  }

  /** Best-effort (hub) analysis IDs of a run, re-resolved from its stored selection descriptor. */
  async getBulkRunAnalysisIds(
    organizationId: string,
    campaignId: string,
    runId: string,
  ): Promise<string[]> {
    const res = await this.fetchApi<{ ids: string[] }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/bulk-runs/${runId}/analysis-ids`,
    );
    return res.ids;
  }

  async getBulkFilesAvailability(
    organizationId: string,
    campaignId: string,
    selection: BulkRunSelection,
  ): Promise<BulkFilesAvailabilityResponse> {
    return this.fetchApi<BulkFilesAvailabilityResponse>(
      `/organizations/${organizationId}/campaigns/${campaignId}/analyses/bulk-files-availability`,
      { method: 'POST', body: JSON.stringify({ selection }) },
    );
  }

  async bulkSelectMedia(
    organizationId: string,
    campaignId: string,
    body: { selection: BulkRunSelection; fileTag: string; analysisType: '2d' | '3d' },
  ): Promise<{ succeeded: number; failed: number; warnings?: string[] }> {
    return this.fetchApi<{ succeeded: number; failed: number; warnings?: string[] }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/analyses/bulk-select-media`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  async bulkSelectTemplate(
    organizationId: string,
    campaignId: string,
    body: { selection: BulkRunSelection; templateId: string },
  ): Promise<{ succeeded: number; failed: number; warnings?: string[] }> {
    return this.fetchApi<{ succeeded: number; failed: number; warnings?: string[] }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/analyses/bulk-select-template`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  async bulkPublish(
    organizationId: string,
    campaignId: string,
    body: { hubAnalysisIds: string[]; enabled: boolean },
  ): Promise<{ succeeded: number }> {
    return this.fetchApi<{ succeeded: number }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/analyses/bulk-publish`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  async bulkDownloadLinks(
    organizationId: string,
    campaignId: string,
    body: { hubAnalysisIds: string[]; fileTags: string[] },
  ): Promise<{ items: Array<{ analysisId: string; title: string; files: Array<{ tag: string; url: string; filename: string }> }> }> {
    return this.fetchApi(
      `/organizations/${organizationId}/campaigns/${campaignId}/analyses/bulk-download-links`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  async buildZipBlob(
    organizationId: string,
    campaignId: string,
    body: { hubAnalysisIds: string[]; fileTags: string[]; includeCsv: boolean },
  ): Promise<Blob> {

    const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/${campaignId}/analyses/bulk-download-zip`;

    const response = await hubFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',

      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error('ZIP-Download fehlgeschlagen.');
    }

    return response.blob();
  }

  /**
   * Fetch lightweight selection rows for every analysis in a campaign in a single
   * request — powers the "select all across pages" flow. Uses ?projection=selection
   * (no pagination, no stats, no hubMetadata blob; capped at 5 000).
   */
  async getCampaignSelectionRows(
    organizationId: string,
    campaignId: string,
    filters?: { states?: string[]; stages?: string[] },
  ): Promise<{ analyses: CampaignSelectionRow[] }> {
    const params = new URLSearchParams({ projection: 'selection' });
    filters?.states?.forEach((s) => params.append('states', s));
    filters?.stages?.forEach((s) => params.append('stages', s));
    return this.fetchApi<{ analyses: CampaignSelectionRow[] }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/analyses?${params.toString()}`,
    );
  }

  /**
   * Subscribe to bulk-run SSE progress for a given runId.
   * Emits counter snapshots and completes on run_complete.
   */
  watchBulkRunEvents(
    organizationId: string,
    campaignId: string,
    runId: string,
  ): Observable<BulkRunEvent> {
    return new Observable((subscriber: Subscriber<BulkRunEvent>) => {

      const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/${campaignId}/bulk-runs/${runId}/events`;
      const ctrl = new AbortController();
      let buffer = '';

      hubFetch(url, {
        headers: {  },
        signal: ctrl.signal,
      })
        .then((response) => {
          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          const pump = (): Promise<void> =>
            reader.read().then(({ done, value }) => {
              if (done) {
                subscriber.complete();
                return;
              }
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() ?? '';
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const event = JSON.parse(line.slice(6)) as BulkRunEvent;
                    subscriber.next(event);
                    if (event.type === 'run_complete') {
                      subscriber.complete();
                      return;
                    }
                  } catch {
                    // ignore malformed lines
                  }
                }
              }
              return pump();
            });
          return pump();
        })
        .catch((err) => {
          if (!ctrl.signal.aborted) subscriber.error(err);
        });

      return () => ctrl.abort();
    });
  }

  /**
   * Download the CSV address template for campaign uploads.
   *
   * Triggers a browser file download without leaving the page.
   * The file includes all supported columns (required + optional energy fields)
   * and three example rows.
   */
  async downloadCsvTemplate(organizationId: string): Promise<void> {

    const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/csv-template`;

    const response = await hubFetch(url, {
      headers: {

      },
    });

    if (!response.ok) {
      await this.handleApiError(response);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'kampagnen-adressen-vorlage.csv';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Release the object URL after a short delay to allow the download to start
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  /**
   * Upload and validate a CSV file for a campaign.
   * Returns validation summary and a preview of the first 10 valid rows.
   */
  async uploadCsv(
    organizationId: string,
    campaignId: string,
    file: File,
  ): Promise<any> {

    const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/${campaignId}/upload-csv`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await hubFetch(url, {
      method: 'POST',
      headers: {},
      body: formData,
    });

    if (!response.ok) {
      await this.handleApiError(response);
    }
    return response.json();
  }

  /**
   * Start bulk processing for a campaign.
   * Requires a prior successful CSV upload.
   */
  async startProcessing(
    organizationId: string,
    campaignId: string,
  ): Promise<{ analysesCreated: number }> {
    return this.fetchApi<{ analysesCreated: number }>(
      `/organizations/${organizationId}/campaigns/${campaignId}/start`,
      { method: 'POST' },
    );
  }

  /**
   * Export campaign results as a CSV download.
   */
  async exportCampaign(
    organizationId: string,
    campaignId: string,
    format: 'csv' | 'excel' = 'csv',
  ): Promise<void> {

    const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/${campaignId}/export?format=${format}`;

    const response = await hubFetch(url, {
      headers: {},
    });

    if (!response.ok) {
      throw new Error('Export fehlgeschlagen.');
    }

    const blob = await response.blob();
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `campaign-${campaignId}.${format === 'excel' ? 'xls' : 'csv'}`;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(anchor.href), 1000);
  }

  // ─── Business Logic Methods ─────────────────────────────────────────────────

  /**
   * Mark a campaign as paid.
   */
  async markPaid(
    organizationId: string,
    campaignId: string,
    dto: MarkPaidDto,
  ): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns/${campaignId}/mark-paid`,
      {
        method: 'POST',
        body: JSON.stringify(dto),
      },
    );
  }

  /**
   * Get real-time progress for a campaign.
   */
  async getProgress(
    organizationId: string,
    campaignId: string,
  ): Promise<CampaignProgress> {
    return this.fetchApi<CampaignProgress>(
      `/organizations/${organizationId}/campaigns/${campaignId}/progress`,
    );
  }

  /**
   * Archive a campaign.
   */
  async archiveCampaign(
    organizationId: string,
    campaignId: string,
  ): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns/${campaignId}/archive`,
      { method: 'POST' },
    );
  }

  /**
   * Unarchive a campaign.
   */
  async unarchiveCampaign(
    organizationId: string,
    campaignId: string,
  ): Promise<Campaign> {
    return this.fetchApi<Campaign>(
      `/organizations/${organizationId}/campaigns/${campaignId}/unarchive`,
      { method: 'POST' },
    );
  }

  // ─── Package Configuration ────────────────────────────────────────────────

  /**
   * Fetch package configuration (pricing, ranges) from the backend.
   * This is the single source of truth for package tiers and pricing.
   */
  async getPackageConfig(
    organizationId: string,
  ): Promise<Record<PackageType, PackageConfig>> {
    return this.fetchApi<Record<PackageType, PackageConfig>>(
      `/organizations/${organizationId}/campaigns/package-config`,
    );
  }

  // ─── Address Resolution ───────────────────────────────────────────────────

  /**
   * Resolve postal codes to addresses via the SI API.
   * Returns the total count and a preview of the first addresses.
   */
  async resolvePostalCodes(
    organizationId: string,
    postalCodes: string[],
  ): Promise<{ totalAddresses: number }> {
    return this.fetchApi<{ totalAddresses: number }>(
      `/organizations/${organizationId}/campaigns/resolve-postal-codes`,
      { method: 'POST', body: JSON.stringify({ postalCodes }) },
    );
  }

  async getOrganizationAddresses(
    organizationId: string,
    options?: { search?: string; page?: number; limit?: number },
  ): Promise<{ data: import('@shared/models/address.model').Address[]; meta: { total: number; page: number; limit: number; pages: number } }> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));
    const qs = params.toString();
    return this.fetchApi(`/organizations/${organizationId}/addresses${qs ? `?${qs}` : ''}`);
  }

  async storeAddressDraft(
    organizationId: string,
    addresses: Array<{ street: string; houseNumber: string; postalCode: string; city: string; country?: string; latitude?: number; longitude?: number }>,
  ): Promise<{ draftId: string; totalAddresses: number }> {
    return this.fetchApi(`/organizations/${organizationId}/campaigns/store-address-draft`, {
      method: 'POST',
      body: JSON.stringify({ addresses }),
    });
  }

  /** Campaign by area: every address inside the drawn areas, stored as a draft like the PLZ path. */
  async fetchAreaAddresses(
    organizationId: string,
    polygons: [number, number][][],
  ): Promise<{ totalAddresses: number; addresses: AddressPreview[]; preview: AddressPreview[]; draftId: string; outliers: OutlierAddress[]; outlierCount: number; areaKm2: number; skippedIncomplete: number }> {
    return this.fetchApi(`/organizations/${organizationId}/campaigns/fetch-area-addresses`, {
      method: 'POST',
      body: JSON.stringify({ polygons }),
    });
  }

  async searchPlaces(organizationId: string, query: string): Promise<PlaceResult[]> {
    const response = await this.fetchApi<{ data: PlaceResult[] }>(
      `/organizations/${organizationId}/campaigns/place-search?q=${encodeURIComponent(query)}`,
    );
    return response.data;
  }

  async fetchPostalCodeAddresses(
    organizationId: string,
    postalCodes: string[],
  ): Promise<{ totalAddresses: number; addresses: AddressPreview[]; preview: AddressPreview[]; draftId: string; outliers: OutlierAddress[]; outlierCount: number }> {
    return this.fetchApi<{
      totalAddresses: number;
      addresses: AddressPreview[];
      preview: AddressPreview[];
      draftId: string;
      outliers: OutlierAddress[];
      outlierCount: number;
    }>(`/organizations/${organizationId}/campaigns/fetch-postal-addresses`, {
      method: 'POST',
      body: JSON.stringify({ postalCodes }),
    });
  }

  /**
   * Validate a CSV file without associating it with a campaign.
   * Returns validation summary with valid/invalid/duplicate counts and full address list.
   */
  async validateCsv(
    organizationId: string,
    file: File,
  ): Promise<CsvValidationResult> {

    const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/validate-csv`;

    const formData = new FormData();
    formData.append('file', file);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60_000);

    let response: Response;
    try {
      response = await hubFetch(url, {
        method: 'POST',
        headers: {},
        body: formData,
        signal: controller.signal,
      });
    } catch (err) {
      const isTimeout = err instanceof DOMException && err.name === 'AbortError';
      throw new Error(isTimeout ? 'Zeitüberschreitung beim Hochladen der CSV-Datei.' : String(err));
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok) {
      await this.handleApiError(response);
    }
    return response.json();
  }

  /**
   * Validate addresses via the SI API using a stored draft ID.
   * Returns SI validation result plus a validatedDraftId containing only passing addresses.
   */
  async validateAddresses(
    organizationId: string,
    draftId: string,
  ): Promise<SiValidationResult> {
    return this.fetchApi<SiValidationResult>(
      `/organizations/${organizationId}/campaigns/validate-area`,
      {
        method: 'POST',
        body: JSON.stringify({ draftId }),
      },
    );
  }

  /**
   * Subscribe to campaign SI setup events via SSE (fetch-based to support Authorization header).
   * Emits events: { type: 'started' | 'complete' | 'failed', ... }
   * The Observable completes when complete or failed is received.
   */
  watchSetupEvents(
    organizationId: string,
    campaignId: string,
  ): Observable<{ type: string; analysesCreated?: number; analysesFailed?: number; error?: string }> {
    return new Observable((subscriber) => {

      const url = `${this.baseApiUrl}/organizations/${organizationId}/campaigns/${campaignId}/setup-events`;
      const ctrl = new AbortController();

      hubFetch(url, {
        headers: {  },
        signal: ctrl.signal,
      })
        .then((response) => {
          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          const pump = (): Promise<void> =>
            reader.read().then(({ done, value }) => {
              if (done) { subscriber.complete(); return; }
              const lines = decoder.decode(value, { stream: true }).split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const event = JSON.parse(line.slice(6));
                    subscriber.next(event);
                    if (event.type === 'complete' || event.type === 'failed') {
                      subscriber.complete();
                      return;
                    }
                  } catch { /* ignore malformed lines */ }
                }
              }
              return pump();
            });
          return pump();
        })
        .catch((err) => { if (!ctrl.signal.aborted) subscriber.error(err); });

      return () => ctrl.abort();
    });
  }
}
